import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { fetchBidAuditHistoryByReferenceNumber } from "../../api/bid";
import { BID_STATUS } from "../../util/bid/bidConstants";
import { getScrubbedBidAuditHistory } from "../../util/bid/bidScrubber";
import { getShiftDate } from "../../util/dateTimeFormatter.js";
import SlideUpTransition from "../fundamentals/SlideUpTransition";
import StarsCloseIconButton from "../fundamentals/StarsCloseIconButton.jsx";
import BidScheduledStatusChip from "./BidScheduledStatusChip.jsx";
import { useSelector } from "react-redux";
import { selectStationFilter } from "../../slices/userSession/userSessionSlice.js";

const BidHistoryDialog = (props) => {
  const stationFilter = useSelector(selectStationFilter);
  const [bidAuditHistoryData, setBidAuditHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleClickDialogClose = () => {
    setBidAuditHistoryData([]);
    props.setSelectedBidForAuditHistory("");
    setIsLoading(false);
  };

  useEffect(() => {
    if (props.selectedBidForAuditHistory?.referenceNumber) {
      setIsLoading(true);
      fetchBidAuditHistoryByReferenceNumber(props.selectedBidForAuditHistory.referenceNumber)
        .then((res) => res.json())
        .then((res) => {
          setIsLoading(false);
          let filteredEvents = res.events;

          // sort the events in reverse chronological order
          filteredEvents.sort((a, b) => parseInt(b.createdAt) - parseInt(a.createdAt));
          const scrubbedEvents = getScrubbedBidAuditHistory(filteredEvents, stationFilter);
          setBidAuditHistoryData(scrubbedEvents);
        })
        .catch(() => {
          console.log("error fetching bid events");
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.selectedBidForAuditHistory?.referenceNumber]);

  const renderSchedDate = (bid) => {
    const schedEndDate = bid.schedEndDate ? getShiftDate(bid.schedEndDate) : "";
    return bid.schedStartDate ? `${getShiftDate(bid.schedStartDate)} - ${schedEndDate}` : "";
  };

  const prepareTitle = () => {
    let title = `${props.selectedBidForAuditHistory?.station} | ${props.selectedBidForAuditHistory?.workgroup}`;

    const effectiveDates = renderSchedDate(props.selectedBidForAuditHistory);

    if (effectiveDates) {
      title = title + " | " + effectiveDates;
    }

    if (props.selectedBidForAuditHistory?.bidName) {
      title = title + " | " + props.selectedBidForAuditHistory?.bidName;
    }

    return title;
  };

  return (
    <Dialog
      className="detail-dialog"
      data-testid="bid-audit-history-dialog"
      open={!!props.selectedBidForAuditHistory}
      TransitionComponent={SlideUpTransition}
      keepMounted
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
      PaperProps={{
        sx: {
          minHeight: "10vh",
          minWidth: "50vw",
        },
      }}
    >
      <DialogTitle>
        <StarsCloseIconButton onClick={handleClickDialogClose} />
        <Box
          sx={{
            overflowWrap: "break-word",
            marginTop: "20px",
          }}
          className="detail-dialog-section-title"
        >
          {prepareTitle()}
        </Box>
      </DialogTitle>
      <DialogContent className="detail-dialog-content">
        <Grid container>
          <Grid item xs={12}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              bidAuditHistoryData?.map((event, index) => {
                if (event.diffs.length === 0) return "";
                return (
                  <Paper
                    key={event.createdAt}
                    sx={{
                      padding: "20px",
                      marginBottom: "10px",
                    }}
                    elevation={3}
                  >
                    <Grid container>
                      <Grid item xs={6}>
                        <Grid container alignItems="center">
                          <Box
                            sx={{
                              display: "inline",
                            }}
                            className="detail-dialog-section-title"
                          >
                            {event.status}
                          </Box>
                          {event.status === BID_STATUS.PENDING && (
                            <Box
                              sx={{
                                marginLeft: "5px",
                                display: "inline",
                              }}
                            >
                              <BidScheduledStatusChip scheduledStatus={event.scheduledStatus} />
                            </Box>
                          )}
                        </Grid>
                        <div>{event.createdAt.toFormat("MM/dd/yyyy T ZZZZ")}</div>
                      </Grid>
                      {!event.isSystemCreated && (
                        <Grid item xs={6} className="detail-dialog-right-text detail-dialog-section-title">
                          <div>
                            {event.modifiedBy.lastName}, {event.modifiedBy.firstName}
                          </div>
                        </Grid>
                      )}

                      {event.comments && (
                        <Grid item xs={12} mt={1} mb={1}>
                          <Typography sx={{ color: "primary.swaBlue", overflowWrap: "break-word" }}>
                            {event.comments}
                          </Typography>
                        </Grid>
                      )}

                      <Accordion
                        TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
                        sx={{ width: "100%", borderRadius: "5px" }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          aria-controls="diffs-content"
                          id="diffs-header"
                        >
                          <Typography component="span">Differences</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer component={Paper}>
                            <Table aria-label="simple table" sx={{ tableLayout: "fixed" }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: "bold" }}>Key</TableCell>
                                  <TableCell sx={{ fontWeight: "bold" }}>Old Value</TableCell>
                                  <TableCell sx={{ fontWeight: "bold" }}>New Value</TableCell>
                                </TableRow>
                              </TableHead>

                              <TableBody>
                                {event.diffs.map((diff) => (
                                  <TableRow
                                    key={diff.fullKey}
                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                  >
                                    <TableCell
                                      component="th"
                                      scope="row"
                                      sx={{
                                        wordWrap: "break-word",
                                        whiteSpace: "normal",
                                      }}
                                    >
                                      {diff.fullKey}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        wordWrap: "break-word",
                                        whiteSpace: "normal",
                                      }}
                                    >
                                      {diff.oldValue
                                        ? Array.isArray(diff.oldValue)
                                          ? diff.oldValue.join(", ")
                                          : diff.oldValue.toString()
                                        : "-"}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        wordWrap: "break-word",
                                        whiteSpace: "normal",
                                      }}
                                    >
                                      {diff.newValue
                                        ? Array.isArray(diff.newValue)
                                          ? diff.newValue.join(", ")
                                          : diff.newValue.toString()
                                        : "-"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    </Grid>
                  </Paper>
                );
              })
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

BidHistoryDialog.propTypes = {
  selectedBidForAuditHistory: PropTypes.object.isRequired,
  setSelectedBidForAuditHistory: PropTypes.func.isRequired,
};

export default BidHistoryDialog;
